import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import './MeetHost.css';
import { useGoogleTranslate } from '../GoogleTranslate'; // Adjust import path as needed

const MeetYourHost = ({ defaultLanguage }) => {
  const { getCurrentLanguage } = useGoogleTranslate();
  const [currentLanguage, setCurrentLanguage] = useState(defaultLanguage || 'en');
  
  // Listen for language changes
  useEffect(() => {
    const checkLanguage = () => {
      const lang = getCurrentLanguage();
      setCurrentLanguage(lang);
    };
    
    // Initial check
    checkLanguage();
    
    // Set up an interval to check for language changes
    const intervalId = setInterval(checkLanguage, 1000);
    
    return () => clearInterval(intervalId);
  }, [getCurrentLanguage, defaultLanguage]);
  

  const scrollToForm = () => {
    const formSection = document.getElementById('form-section');
    if (formSection) {
      // Get the position of the element relative to the viewport
      const elementPosition = formSection.getBoundingClientRect().top;
      // Get the current scroll position
      const offsetPosition = elementPosition + window.pageYOffset;
      // Add an offset (e.g., -80px) to adjust the final position
      const offsetValue = -80; // Adjust this value as needed
      
      // Scroll to the element with the offset
      window.scrollTo({
        top: offsetPosition + offsetValue,
        behavior: 'smooth'
      });
    }
  };
  
  // Function to redirect to AskAngel website
  const redirectToAskAngel = () => {
    window.open('https://www.askangel.ai/moneyformynexthome', '_blank');
  };

  // Google Maps URL for the address
  const mapsUrl = 'https://www.google.com/maps/place/Maramar+Plaza/@18.4094999,-66.1062349,17z/data=!3m1!4b1!4m6!3m5!1s0x8c036905d0a1b93f:0xc57fb0cface55267!8m2!3d18.4094999!4d-66.10366!16s%2Fg%2F1hc110ccy?entry=ttu&g_ep=EgoyMDI5MDQzMC4xIKXMDSoASAFQAw%3D%3D';

  // Language-specific content
  const content = {
    en: {
      title: "Meet Your Host: ",
      hostName: "Joaquin Claudio",
      descriptions: [
        "With over a decade in mortgage lending and real estate, Joaquin Claudio has helped hundreds of individuals and families navigate the path to homeownership.",
        "His mission through The Way to Your Own Home is to make the homebuying process more accessible, less intimidating, and completely transparent. In his own words, \"Everyone deserves a fair shot at homeownership—with the right knowledge and guidance, it's possible.\"",
        "Click the link below to register for the webinar or tap \"Ask Me Anything\" to chat with Joaquin directly."
      ],
      contactName: "Joaquin Claudio",
      contactTitle: "NMLS 379270 | Mortgage Loan Officer",
      phone: "(407) 378-1082",
      phoneLabel: "D: ",
      email: "joaquin.claudio@swmc.com",
      website: "joaquinclaudio.sunwestmortgage.com",
      websiteUrl: "https://joaquinclaudio.sunwestmortgage.com/",
      address: [
        "101 San Patricio Avenue,",
        "Maramar Building Suite G1",
        "Guaynabo, PR 00968"
      ],
      branchInfo: "Branch NMLS 516931",
      reserveButton: "Reserve Your Spot",
      askButton: "Ask Me Anything"
    },
    es: {
      title: "Conoce a Tu Anfitrión: ",
      hostName: "Joaquin Claudio",
      descriptions: [
        "Con más de una década en el préstamo hipotecario y bienes raíces, Joaquin Claudio ha ayudado a cientos de individuos y familias a navegar el camino hacia la propiedad de vivienda.",
        "Su misión a través de El Camino a Tu Propia Casa es hacer que el proceso de compra de vivienda sea más accesible, menos intimidante y completamente transparente. En sus propias palabras, \"Todos merecen una oportunidad justa en la propiedad de vivienda—con el conocimiento y la orientación correctos, es posible.\"",
        "Haz clic en el enlace a continuación para registrarte en el seminario web o toca \"Pregúntame Cualquier Cosa\" para chatear directamente con Joaquin."
      ],
      contactName: "Joaquin Claudio",
      contactTitle: "NMLS 379270 | Oficial de Préstamos Hipotecarios",
      phone: "(407) 378-1082",
      phoneLabel: "D: ",
      email: "joaquin.claudio@swmc.com",
      website: "joaquinclaudio.sunwestmortgage.com",
      websiteUrl: "https://joaquinclaudio.sunwestmortgage.com/",
      address: [
        "101 Avenida San Patricio,",
        "Edificio Maramar Suite G1",
        "Guaynabo, PR 00968"
      ],
      branchInfo: "Sucursal NMLS 516931",
      reserveButton: "Reserva Tu Lugar",
      askButton: "Pregúntame Cualquier Cosa"
    }
  };
  
  // Select the appropriate content based on current language
  const {
    title,
    hostName,
    descriptions,
    contactName,
    contactTitle,
    phone,
    phoneLabel,
    email,
    website,
    websiteUrl,
    address,
    branchInfo,
    reserveButton,
    askButton
  } = content[currentLanguage] || content.en;
  
  return (
    <Container className="meet-host-container">
      <Row>
        <Col lg={5}>
          <h2 className="host-title notranslate" translate="no">
            {title}<br className='brtag'/><span className="host-name">{hostName}</span>
          </h2>
          
          {descriptions.map((description, index) => (
            <p key={`desc-${index}`} className="host-description notranslate" translate="no">
              {description}
            </p>
          ))}
        </Col>
        
        <Col lg={7} className="right-column">
          <div className="host-section">
            <div className="host-image-container">
              <div className="image-border"></div>
              <div className="host-image">
                <img 
                src={`${process.env.REACT_APP_CDN_PATH_THEWAYTOYOUROWNHOME}assets/joaquin-claudio.webp`}
                  alt={`${hostName}, Mortgage Loan Officer`} 
                />
              </div>
            </div>
            
            <div className="host-contact-info">
              <h3 className="contact-name notranslate" translate="no">{contactName}</h3>
              <p className="contact-title notranslate" translate="no">{contactTitle}</p>
              <p className="contact-detail notranslate" translate="no">
                <span className="phone-label" style={{textDecoration: 'none', color: '#050344'}}>{phoneLabel}</span>
                <a href={`tel:${phone.replace(/\D/g, '')}`} style={{textDecoration: 'none', color: '#050344'}}>{phone}</a>
              </p>
              <p className="contact-detail notranslate" translate="no">
                <a href={`mailto:${email}`} style={{textDecoration: 'none', color: '#050344'}}>{email}</a>
              </p>
              <p className="contact-detail notranslate" translate="no">
                <a href={websiteUrl} target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none', color: '#050344'}}>{website}</a>
              </p>
              <a 
                href={mapsUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="contact-address notranslate" 
                translate="no"
              >
                {address[0]}<br />
                {address[1]}<br />
                {address[2]}
              </a>
              <p className="branch-info notranslate" translate="no">{branchInfo}</p>
            </div>
          </div>
          
          <div className="buttons-row">
            <Button 
              variant="primary" 
              className="reserve-spot-btn notranslate" 
              translate="no" 
              onClick={scrollToForm}
            >
              {reserveButton}
            </Button>
            <Button 
              variant="warning" 
              className="ask-me-btn notranslate" 
              translate="no" 
              onClick={redirectToAskAngel}
            >
              {askButton}
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default MeetYourHost;